// components/LandingPage.js - Landing Page Complète et Moderne pour WorkBiFlow
import React, { useState, useEffect } from 'react';
import { 
  Clock, Users, BarChart3, Calendar, DollarSign, File, 
  Shield, Zap, Globe, CheckCircle, ArrowRight, Star,
  TrendingUp, Award, Smartphone, Lock, HeadphonesIcon,
  CreditCard, MapPin, Camera, QrCode, BookOpen,
  Menu, X, History, CalendarDays, CalendarClock, Lightbulb,
  Megaphone, FileText, GraduationCap, Wallet, UserCircle
} from 'lucide-react';
import { API_BASE } from '../config';
import { useTranslation } from '../i18n/LanguageContext';
import { PLAN_MODULE_KEYS, PLAN_META } from '../planModules';
import LanguageSelector from './LanguageSelector';
import WorkBiFlowLogo from './WorkBiFlowLogo';

const LandingPage = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const features = [
    {
      icon: <Clock size={32} />,
      title: t('landing.feature_pointage'),
      description: t('landing.feature_pointage_desc')
    },
    {
      icon: <History size={32} />,
      title: t('landing.feature_historique'),
      description: t('landing.feature_historique_desc')
    },
    {
      icon: <CalendarDays size={32} />,
      title: t('landing.feature_calendrier'),
      description: t('landing.feature_calendrier_desc')
    },
    {
      icon: <CalendarClock size={32} />,
      title: t('landing.feature_planning'),
      description: t('landing.feature_planning_desc')
    },
    {
      icon: <Lightbulb size={32} />,
      title: t('landing.feature_ideas'),
      description: t('landing.feature_ideas_desc')
    },
    {
      icon: <Megaphone size={32} />,
      title: t('landing.feature_communiques'),
      description: t('landing.feature_communiques_desc')
    },
    {
      icon: <BarChart3 size={32} />,
      title: t('landing.feature_dashboard'),
      description: t('landing.feature_dashboard_desc')
    },
    {
      icon: <Users size={32} />,
      title: t('landing.feature_team'),
      description: t('landing.feature_team_desc')
    },
    {
      icon: <FileText size={32} />,
      title: t('landing.feature_reports'),
      description: t('landing.feature_reports_desc')
    },
    {
      icon: <QrCode size={32} />,
      title: t('landing.feature_badges'),
      description: t('landing.feature_badges_desc')
    },
    {
      icon: <GraduationCap size={32} />,
      title: t('landing.feature_formations'),
      description: t('landing.feature_formations_desc')
    },
    {
      icon: <DollarSign size={32} />,
      title: t('landing.feature_expenses'),
      description: t('landing.feature_expenses_desc')
    },
    {
      icon: <Calendar size={32} />,
      title: t('landing.feature_leaves'),
      description: t('landing.feature_leaves_desc')
    },
    {
      icon: <Wallet size={32} />,
      title: t('landing.feature_soldes'),
      description: t('landing.feature_soldes_desc')
    },
    {
      icon: <File size={32} />,
      title: t('landing.feature_documents'),
      description: t('landing.feature_documents_desc')
    },
    {
      icon: <UserCircle size={32} />,
      title: t('landing.feature_profil'),
      description: t('landing.feature_profil_desc')
    },
    {
      icon: <Camera size={32} />,
      title: t('landing.feature_audit'),
      description: t('landing.feature_audit_desc')
    }
  ];

  // Plans from shared config: one source for landing + module plan (automatic setup)
  const planIds = ['starter', 'business', 'enterprise'];
  const plans = planIds.map((id) => {
    const meta = PLAN_META[id];
    const featureKeys = PLAN_MODULE_KEYS[id] || [];
    return {
      name: meta.name,
      price: meta.price,
      priceEur: meta.price,
      pricePerEmployee: meta.pricePerEmployee,
      maxEmployees: meta.maxEmployees,
      period: t('landing.plan_per_month'),
      features: featureKeys.map((key) => t(`landing.${key}`)),
      popular: meta.popular
    };
  });

  const stats = [
    { value: '500+', label: t('landing.stats_companies') },
    { value: '10K+', label: t('landing.stats_users') },
    { value: '99.9%', label: t('landing.stats_availability') },
    { value: '24/7', label: t('landing.stats_support') }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="landing-nav-brand">
          <WorkBiFlowLogo size={28} />
          <span>WorkBiFlow</span>
        </div>
        <div className="landing-nav-desktop">
          <LanguageSelector />
          <a href="#features" onClick={() => setIsMobileMenuOpen(false)}>{t('landing.features_title')}</a>
          <a href="#pricing" onClick={() => setIsMobileMenuOpen(false)}>{t('landing.pricing_title')}</a>
          <button
            className="landing-nav-button"
            onClick={() => onNavigate && onNavigate('login')}
          >
            {t('auth.login')}
          </button>
        </div>
        <button 
          className="landing-nav-mobile-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        {isMobileMenuOpen && (
          <div className="landing-nav-mobile">
            <LanguageSelector />
            <a href="#features" onClick={() => setIsMobileMenuOpen(false)}>{t('landing.features_title')}</a>
            <a href="#pricing" onClick={() => setIsMobileMenuOpen(false)}>{t('landing.pricing_title')}</a>
            <button
              className="landing-nav-button"
              onClick={() => {
                setIsMobileMenuOpen(false);
                onNavigate && onNavigate('login');
              }}
            >
              {t('auth.login')}
            </button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="landing-hero">
        <div style={{
          fontSize: 'clamp(14px, 2vw, 16px)',
          color: '#00d4ff',
          fontWeight: 600,
          marginBottom: '20px',
          letterSpacing: '2px',
          textTransform: 'uppercase'
        }}>
          🚀 {t('landing.subtitle')}
        </div>
        <h1 style={{
          fontSize: 'clamp(40px, 8vw, 72px)',
          fontWeight: 800,
          marginBottom: 'clamp(18px, 3vw, 24px)',
          lineHeight: 1.1,
          background: 'linear-gradient(135deg, #ffffff 0%, #00d4ff 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          {t('landing.title')}
        </h1>
        <p style={{
          fontSize: 'clamp(18px, 3vw, 24px)',
          color: 'rgba(255, 255, 255, 0.7)',
          marginBottom: '40px',
          maxWidth: '700px',
          margin: '0 auto 40px',
          lineHeight: 1.6
        }}>
          {t('landing.description')}
        </p>
        <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => onNavigate && onNavigate('register')}
            style={{
              background: 'linear-gradient(135deg, #00d4ff, #00a8cc)',
              color: '#ffffff',
              border: 'none',
              padding: 'clamp(14px, 2vw, 18px) clamp(28px, 4vw, 36px)',
              borderRadius: '12px',
              fontSize: 'clamp(16px, 2.5vw, 18px)',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              transition: 'all 0.3s',
              boxShadow: '0 8px 24px rgba(0, 212, 255, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 12px 32px rgba(0, 212, 255, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 212, 255, 0.3)';
            }}
          >
            {t('landing.get_started')}
            <ArrowRight size={20} />
          </button>
          <button
            onClick={() => {
              const element = document.getElementById('features');
              element?.scrollIntoView({ behavior: 'smooth' });
            }}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              padding: 'clamp(14px, 2vw, 18px) clamp(28px, 4vw, 36px)',
              borderRadius: '12px',
              fontSize: 'clamp(16px, 2.5vw, 18px)',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.transform = 'translateY(-4px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {t('common.more') || 'En savoir plus'}
          </button>
        </div>

        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 'clamp(24px, 4vw, 40px)',
          marginTop: 'clamp(60px, 8vw, 80px)',
          padding: 'clamp(24px, 4vw, 40px)',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '20px',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          maxWidth: '1200px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          {stats.map((stat, index) => (
            <div key={index} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: 800, color: '#00d4ff', marginBottom: '8px' }}>
                {stat.value}
              </div>
              <div style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.7)' }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="landing-section">
        <div style={{ textAlign: 'center', marginBottom: 'clamp(40px, 6vw, 60px)' }}>
          <h2 style={{
            fontSize: 'clamp(32px, 6vw, 48px)',
            fontWeight: 800,
            marginBottom: 'clamp(12px, 2vw, 16px)',
            background: 'linear-gradient(135deg, #ffffff 0%, #00d4ff 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            {t('landing.powerful_features')}
          </h2>
          <p style={{
            fontSize: 'clamp(16px, 2.5vw, 20px)',
            color: 'rgba(255, 255, 255, 0.7)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            {t('landing.features_description')}
          </p>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 'clamp(24px, 4vw, 32px)',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {features.map((feature, index) => (
            <div
              key={index}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '20px',
                padding: 'clamp(24px, 4vw, 32px)',
                transition: 'all 0.3s',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(0, 212, 255, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
              }}
            >
              <div style={{
                width: '64px',
                height: '64px',
                background: 'linear-gradient(135deg, #00d4ff, #00a8cc)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 'clamp(16px, 2.5vw, 20px)',
                color: '#ffffff'
              }}>
                {feature.icon}
              </div>
              <h3 style={{
                fontSize: 'clamp(20px, 3vw, 24px)',
                fontWeight: 700,
                marginBottom: 'clamp(10px, 1.5vw, 12px)',
                color: '#ffffff'
              }}>
                {feature.title}
              </h3>
              <p style={{
                fontSize: '16px',
                color: 'rgba(255, 255, 255, 0.7)',
                lineHeight: 1.6
              }}>
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="landing-section landing-section-alt">
        <div style={{ textAlign: 'center', marginBottom: 'clamp(40px, 6vw, 60px)' }}>
          <h2 style={{
            fontSize: 'clamp(32px, 6vw, 48px)',
            fontWeight: 800,
            marginBottom: '16px',
            background: 'linear-gradient(135deg, #ffffff 0%, #00d4ff 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            {t('landing.pricing_transparent')}
          </h2>
          <p style={{
            fontSize: 'clamp(16px, 2.5vw, 20px)',
            color: 'rgba(255, 255, 255, 0.7)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            {t('landing.choose_plan')}
          </p>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '32px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {plans.map((plan, index) => (
            <div
              key={index}
              style={{
                background: plan.popular 
                  ? 'linear-gradient(135deg, rgba(0, 212, 255, 0.2), rgba(0, 168, 204, 0.1))'
                  : 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(20px)',
                border: plan.popular 
                  ? '2px solid rgba(0, 212, 255, 0.5)'
                  : '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '20px',
                padding: 'clamp(32px, 5vw, 40px)',
                position: 'relative',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 16px 48px rgba(0, 212, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {plan.popular && (
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'linear-gradient(135deg, #00d4ff, #00a8cc)',
                  color: '#ffffff',
                  padding: '6px 20px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  {t('landing.plan_popular')}
                </div>
              )}
              <h3 style={{
                fontSize: 'clamp(24px, 3.5vw, 28px)',
                fontWeight: 700,
                marginBottom: 'clamp(10px, 1.5vw, 12px)',
                color: '#ffffff'
              }}>
                {plan.name}
              </h3>
              <div style={{ marginBottom: 'clamp(24px, 4vw, 32px)' }}>
                <span style={{
                  fontSize: 'clamp(30px, 6vw, 56px)',
                  fontWeight: 800,
                  color: '#00d4ff'
                }}>
                  {plan.pricePerEmployee || plan.price} EUR
                </span>
                <span style={{
                  fontSize: '15px',
                  color: 'rgba(255, 255, 255, 0.7)',
                  marginLeft: '8px'
                }}>
                  {`/${plan.period} /${t('employee')}`}
                </span>
                <div style={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.6)',
                  marginTop: '4px'
                }}>
                  {plan.pricePerEmployee
                    ? `≈ ${Math.round(parseFloat(String(plan.pricePerEmployee).replace(',', '.')) * 655).toLocaleString('en-US')} FCFA/${t('landing.per_employee')}`
                    : `≈ ${Math.round(parseFloat(String(plan.price || '0').replace(',', '.')) * 655).toLocaleString('en-US')} FCFA`
                  }
                </div>
              </div>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                marginBottom: 'clamp(24px, 4vw, 32px)'
              }}>
                {plan.features.map((feature, fIndex) => (
                  <li key={fIndex} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'clamp(10px, 1.5vw, 12px)',
                    marginBottom: 'clamp(12px, 2vw, 16px)',
                    fontSize: 'clamp(14px, 2vw, 16px)',
                    color: 'rgba(255, 255, 255, 0.8)'
                  }}>
                    <CheckCircle size={20} color="#00d4ff" />
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => onNavigate && onNavigate('register')}
                style={{
                  width: '100%',
                  background: plan.popular
                    ? 'linear-gradient(135deg, #00d4ff, #00a8cc)'
                    : 'rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                  border: plan.popular ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                  padding: 'clamp(12px, 2vw, 14px) clamp(20px, 3vw, 24px)',
                  borderRadius: '12px',
                  fontSize: 'clamp(14px, 2vw, 16px)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 212, 255, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {t('landing.start_now')}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="landing-section landing-cta">
        <h2 style={{
          fontSize: 'clamp(32px, 6vw, 48px)',
          fontWeight: 800,
          marginBottom: 'clamp(18px, 3vw, 24px)',
          background: 'linear-gradient(135deg, #ffffff 0%, #00d4ff 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          {t('landing.ready_to_transform')}
        </h2>
        <p style={{
          fontSize: 'clamp(16px, 2.5vw, 20px)',
          color: 'rgba(255, 255, 255, 0.7)',
          marginBottom: 'clamp(30px, 5vw, 40px)'
        }}>
          {t('landing.join_companies')}
        </p>
        <button
          onClick={() => onNavigate && onNavigate('register')}
          style={{
            background: 'linear-gradient(135deg, #00d4ff, #00a8cc)',
            color: '#ffffff',
            border: 'none',
            padding: 'clamp(16px, 2.5vw, 20px) clamp(32px, 5vw, 44px)',
            borderRadius: '12px',
            fontSize: 'clamp(18px, 2.5vw, 20px)',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            margin: '0 auto',
            transition: 'all 0.3s',
            boxShadow: '0 8px 24px rgba(0, 212, 255, 0.3)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 12px 32px rgba(0, 212, 255, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 212, 255, 0.3)';
          }}
        >
          {t('landing.start_free_trial')}
          <ArrowRight size={24} />
        </button>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-content">
          <div>
            <div style={{ fontSize: '24px', fontWeight: 700, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <WorkBiFlowLogo size={28} />
              <span>WorkBiFlow</span>
            </div>
            <p style={{ color: 'rgba(255, 255, 255, 0.6)', lineHeight: 1.6 }}>
              {t('landing.footer_description')}
            </p>
          </div>
          <div>
            <h4 style={{ fontSize: 'clamp(16px, 2.2vw, 18px)', fontWeight: 700, marginBottom: 'clamp(12px, 2vw, 16px)' }}>{t('landing.footer_product')}</h4>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              <li style={{ marginBottom: 'clamp(10px, 1.5vw, 12px)' }}><a href="#features" style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none' }}>{t('landing.footer_features')}</a></li>
              <li style={{ marginBottom: '12px' }}><a href="#pricing" style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none' }}>{t('landing.footer_pricing')}</a></li>
              <li style={{ marginBottom: '12px' }}><a href="#" style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none' }}>{t('landing.footer_security')}</a></li>
            </ul>
          </div>
          <div>
            <h4 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '16px' }}>{t('landing.footer_support')}</h4>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              <li style={{ marginBottom: '12px' }}><a href="/documentation" onClick={(e) => { e.preventDefault(); window.location.href = '/documentation'; }} style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none', cursor: 'pointer' }}>{t('landing.footer_documentation')}</a></li>
              <li style={{ marginBottom: '12px' }}><a href="/contact" onClick={(e) => { e.preventDefault(); window.location.href = '/contact'; }} style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none', cursor: 'pointer' }}>{t('landing.footer_contact')}</a></li>
              <li style={{ marginBottom: '12px' }}><a href="/faq" onClick={(e) => { e.preventDefault(); window.location.href = '/faq'; }} style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none', cursor: 'pointer' }}>{t('landing.footer_faq')}</a></li>
            </ul>
          </div>
          <div>
            <h4 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '16px' }}>{t('landing.footer_legal')}</h4>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              <li style={{ marginBottom: '12px' }}><a href="/cgu" onClick={(e) => { e.preventDefault(); window.location.href = '/cgu'; }} style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none', cursor: 'pointer' }}>{t('landing.footer_terms')}</a></li>
              <li style={{ marginBottom: '12px' }}><a href="/confidentialite" onClick={(e) => { e.preventDefault(); window.location.href = '/confidentialite'; }} style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none', cursor: 'pointer' }}>{t('landing.footer_privacy')}</a></li>
              <li style={{ marginBottom: '12px' }}><a href="/mentions-legales" onClick={(e) => { e.preventDefault(); window.location.href = '/mentions-legales'; }} style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none', cursor: 'pointer' }}>{t('landing.footer_legal_notice')}</a></li>
            </ul>
          </div>
        </div>
        <div style={{
          textAlign: 'center',
          paddingTop: 'clamp(30px, 5vw, 40px)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          color: 'rgba(255, 255, 255, 0.6)',
          fontSize: 'clamp(12px, 1.8vw, 14px)'
        }}>
          {t('landing.footer_copyright')}
        </div>
      </footer>

      <style>{`
        /* Navigation */
        .landing-nav {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          padding: clamp(16px, 3vw, 24px) clamp(16px, 4vw, 40px);
          background: rgba(10, 14, 39, 0.8);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          z-index: 1000;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .landing-nav-brand {
          font-size: clamp(20px, 3vw, 24px);
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 10px;
          color: #ffffff;
        }

        .landing-nav-desktop {
          display: flex;
          gap: clamp(16px, 3vw, 24px);
          align-items: center;
        }

        .landing-nav-desktop a {
          color: #ffffff;
          text-decoration: none;
          font-size: clamp(14px, 2vw, 16px);
          transition: color 0.3s;
        }

        .landing-nav-desktop a:hover {
          color: #00d4ff;
        }

        .landing-nav-button {
          background: linear-gradient(135deg, #00d4ff, #00a8cc);
          color: #ffffff;
          border: none;
          padding: clamp(8px, 1.5vw, 12px) clamp(16px, 3vw, 24px);
          border-radius: 12px;
          font-size: clamp(14px, 2vw, 16px);
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
        }

        .landing-nav-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 212, 255, 0.4);
        }

        .landing-nav-mobile-toggle {
          display: none;
          background: transparent;
          border: none;
          color: #ffffff;
          cursor: pointer;
          padding: 8px;
        }

        .landing-nav-mobile {
          display: none;
        }

        /* Sections */
        .landing-hero,
        .landing-section {
          padding: clamp(100px, 15vw, 200px) clamp(20px, 4vw, 40px) clamp(60px, 8vw, 100px);
          max-width: 1200px;
          margin: 0 auto;
          width: 100%;
          box-sizing: border-box;
        }

        .landing-hero {
          text-align: center;
          padding-top: clamp(140px, 20vw, 240px);
        }

        .landing-section-alt {
          background: rgba(255, 255, 255, 0.02);
        }

        .landing-cta {
          text-align: center;
          max-width: 800px;
          margin: 0 auto;
        }

        /* Footer */
        .landing-footer {
          padding: clamp(40px, 6vw, 60px) clamp(20px, 4vw, 40px) clamp(24px, 4vw, 40px);
          background: rgba(0, 0, 0, 0.3);
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          width: 100%;
          box-sizing: border-box;
        }

        .landing-footer-content {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: clamp(24px, 4vw, 40px);
          margin-bottom: 40px;
        }

        /* Responsive Mobile */
        @media (max-width: 768px) {
          .landing-nav {
            padding: 16px;
          }

          .landing-nav-desktop {
            display: none;
          }

          .landing-nav-mobile-toggle {
            display: block;
          }

          .landing-nav-mobile {
            display: flex;
            flex-direction: column;
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            background: rgba(10, 14, 39, 0.98);
            backdrop-filter: blur(20px);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            padding: 20px 16px;
            gap: 16px;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
          }

          .landing-nav-mobile a {
            color: #ffffff;
            text-decoration: none;
            font-size: 16px;
            padding: 12px 0;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          }

          .landing-nav-mobile a:last-of-type {
            border-bottom: none;
          }

          .landing-nav-mobile .landing-nav-button {
            width: 100%;
            margin-top: 8px;
          }

          .landing-hero,
          .landing-section {
            padding: clamp(120px, 18vw, 160px) 20px clamp(40px, 6vw, 60px);
          }

          .landing-hero {
            padding-top: clamp(140px, 22vw, 180px);
          }

          .landing-footer {
            padding: 40px 20px 24px;
          }

          .landing-footer-content {
            grid-template-columns: 1fr;
            gap: 32px;
          }
        }

        /* Responsive Tablet */
        @media (min-width: 769px) and (max-width: 1024px) {
          .landing-hero,
          .landing-section {
            padding-left: clamp(24px, 4vw, 32px);
            padding-right: clamp(24px, 4vw, 32px);
          }

          .landing-footer {
            padding-left: clamp(24px, 4vw, 32px);
            padding-right: clamp(24px, 4vw, 32px);
          }
        }

        /* Responsive Desktop */
        @media (min-width: 1025px) {
          .landing-hero,
          .landing-section {
            padding-left: 40px;
            padding-right: 40px;
          }

          .landing-footer {
            padding-left: 40px;
            padding-right: 40px;
          }
        }
      `}</style>
    </div>
  );
};

export default LandingPage;





